import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile as OriginalUserProfile } from '@/types';

// Helper function to get initial dark mode state
const getInitialDarkMode = () => {
  const storedPreference = localStorage.getItem('themePreference');
  if (storedPreference === 'dark') {
    return true;
  }
  if (storedPreference === 'light') {
    return false;
  }
  // Default to system preference if no explicit preference is set
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
};

// Ensure UserProfile in the store matches the new structure
export interface UserProfile extends Omit<OriginalUserProfile, 'defaultModelId'> {
  defaultModelApiName?: string;
}

interface AppState {
  user: UserProfile | null;
  setUser: (user: UserProfile | null) => void;
  darkMode: boolean;
  toggleDarkMode: (preference?: 'light' | 'dark' | 'system') => void;
  textSize: 'small' | 'medium' | 'large';
  setTextSize: (size: 'small' | 'medium' | 'large') => void;
  saveHistory: boolean;
  setSaveHistory: (save: boolean) => void;
  allowAnalytics: boolean;
  setAllowAnalytics: (allow: boolean) => void;
  ollamaBaseUrl: string | null;
  setOllamaBaseUrl: (url: string | null) => void;
  cohereKey: string | null;
  setCohereKey: (key: string | null) => void;
}

export const useAppStore = create<AppState>()(
  (set, get) => ({
    user: null,
    setUser: (user) => set({ user }),
    darkMode: getInitialDarkMode(), // Initialize dark mode state

    toggleDarkMode: (preference) => {
      const currentDarkMode = get().darkMode;
      let newDarkMode: boolean;
      let newPreference: 'light' | 'dark' | 'system';

      if (preference === 'system') {
        newDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        newPreference = 'system';
      } else if (preference === 'dark') {
        newDarkMode = true;
        newPreference = 'dark';
      } else if (preference === 'light') {
        newDarkMode = false;
        newPreference = 'light';
      } else {
        // If no preference is passed, just toggle the current state
        newDarkMode = !currentDarkMode;
        newPreference = newDarkMode ? 'dark' : 'light'; // Assume manual toggle
      }

      set({ darkMode: newDarkMode });
      localStorage.setItem('themePreference', newPreference);

      if (newDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    },

    textSize: (localStorage.getItem('textSize') as 'small' | 'medium' | 'large') || 'medium',
    setTextSize: (size) => {
      set({ textSize: size });
      localStorage.setItem('textSize', size);
      document.documentElement.classList.remove('text-size-small', 'text-size-medium', 'text-size-large');
      document.documentElement.classList.add(`text-size-${size}`);
    },
    saveHistory: localStorage.getItem('saveHistory') !== 'false', // Default to true
    setSaveHistory: (save) => {
      set({ saveHistory: save });
      localStorage.setItem('saveHistory', save.toString());
    },
    allowAnalytics: localStorage.getItem('allowAnalytics') !== 'false', // Default to true
    setAllowAnalytics: (allow) => {
      set({ allowAnalytics: allow });
      localStorage.setItem('allowAnalytics', allow.toString());
    },
    ollamaBaseUrl: localStorage.getItem('ollamaBaseUrl') || null,
    setOllamaBaseUrl: (url) => {
      set({ ollamaBaseUrl: url });
      localStorage.setItem('ollamaBaseUrl', url || '');
    },
    cohereKey: localStorage.getItem('cohere_api_key') || null,
    setCohereKey: (key) => {
      set({ cohereKey: key });
      if (key) {
        localStorage.setItem('cohere_api_key', key);
      } else {
        localStorage.removeItem('cohere_api_key');
      }
    },
  }),
  {
    name: 'app-storage',
  }
);

// Apply initial theme on store creation
// This ensures the theme is set as soon as the store is initialized
// and before React components fully render.
const initialDarkMode = getInitialDarkMode();
if (initialDarkMode) {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}

// Apply initial text size on store creation
const initialTextSize = (localStorage.getItem('textSize') as 'small' | 'medium' | 'large') || 'medium';
document.documentElement.classList.remove('text-size-small', 'text-size-medium', 'text-size-large');
document.documentElement.classList.add(`text-size-${initialTextSize}`);

// Listen for system theme changes if preference is 'system'
const storedPreference = localStorage.getItem('themePreference');
if (storedPreference === 'system') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    useAppStore.getState().toggleDarkMode('system');
  });
}